import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { QueryOptionsAlone } from 'apollo-angular/types';
import { FullUserFragment, ListUsersGQL, ListUsersQueryVariables } from 'graphql.generated';
import { catchError, distinctUntilChanged, EMPTY, map, merge, of, startWith, switchMap, timer } from 'rxjs';
import { selectCustomerRole } from 'src/app/job/job-create/job-create-state/job-create.selectors';
import { NewOpportunityCustomerActions } from 'src/app/new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { newOpportunitySimilarJobsFeature } from 'src/app/new-opportunity/new-opportunity-similar-jobs/new-opportunity-similar-jobs.feature';

import { NewOpportunitySearchCustomersDialogActions } from './new-opportunity-search-customers-dialog.actions';

export const prepopulateSearchQuery = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(NewOpportunitySearchCustomersDialogActions.repeatCustomerButtonClicked),
    concatLatestFrom(() => store.select(newOpportunitySimilarJobsFeature.selectQuery)),
    map(([ _action, query ]) => NewOpportunitySearchCustomersDialogActions.searchCustomersDialogOpened({ query })),
  );
}, { functional: true, dispatch: true });

export const searchCustomers = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  listUsersGQL = inject(ListUsersGQL),
) => {
  return merge(
    actions$.pipe(
      ofType(
        NewOpportunitySearchCustomersDialogActions.searchCustomersDialogOpened,
        NewOpportunitySearchCustomersDialogActions.searchQueryChanged,
      ),
      map(({ query }) => query.trim()),
    ),
    actions$.pipe(
      ofType(
        NewOpportunitySearchCustomersDialogActions.searchCustomersDialogClosed,
        NewOpportunitySearchCustomersDialogActions.customerRowClicked,
      ),
      map(() => ''),
    ),
  ).pipe(
    distinctUntilChanged(),
    switchMap((query) => !query ? EMPTY : timer(300).pipe(
      map(() => query),
      concatLatestFrom(() => store.select(selectCustomerRole)),
      map(([ searchQuery, customerRoleId ]): { vars: ListUsersQueryVariables; opts: QueryOptionsAlone<ListUsersQueryVariables> } => ({
        vars: {
          filter: {
            search: searchQuery,
            roles: [customerRoleId],
          },
          skip: 0,
          limit: 25,
        },
        opts: {
          fetchPolicy: 'network-only',
        },
      })),
      switchMap(({ vars, opts }) => listUsersGQL.fetch(vars, opts).pipe(
        map((res) => NewOpportunitySearchCustomersDialogActions.searchCustomersSuccess({
          customers: (res.data?.usersv2?.users ?? []) as FullUserFragment[],
        })),
        startWith(NewOpportunitySearchCustomersDialogActions.searchCustomersStarted()),
      )),
      catchError((error) => of(NewOpportunitySearchCustomersDialogActions.searchCustomersError({
        error,
      }))),
    )),
  );
}, { functional: true, dispatch: true });

export const selectCustomer = createEffect((
  actions$ = inject(Actions),
) => {
  return actions$.pipe(
    ofType(NewOpportunitySearchCustomersDialogActions.customerRowClicked),
    map(({ customer }) => NewOpportunityCustomerActions.existingCustomerSelected({ existingCustomer: customer })),
  );
}, { functional: true, dispatch: true });
